import React, { useState, useEffect } from 'react';
import { api } from '../config/api';

interface TenantEntry {
  tenant: string;
  address: string;
  rent?: number;
  has_pdf?: boolean;
  pdf_url?: string;
}

interface Template {
  name: string;
  filename: string | null;
  pdf_url: string | null;
  pages: number;
  description: string;
  tenants?: TenantEntry[];
  count?: number;
}

interface TemplateData {
  templates: Template[];
  total_leases: number;
  missing_pdf_count: number;
  missing_pdfs: TenantEntry[];
}

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const API_BASE = process.env.REACT_APP_API_URL || '';

export const LeaseTemplates: React.FC = () => {
  const [data, setData] = useState<TemplateData | null>(null);
  const [previewPdf, setPreviewPdf] = useState<{ url: string; name: string } | null>(null);
  const [expandedTemplate, setExpandedTemplate] = useState<number | null>(null);
  const [askInput, setAskInput] = useState('');
  const [askAnswer, setAskAnswer] = useState('');
  const [isAsking, setIsAsking] = useState(false);

  useEffect(() => {
    api.get('/lease-qa/templates').then(res => setData(res.data)).catch(() => {});
  }, []);

  const askQuestion = async () => {
    if (!askInput.trim() || isAsking) return;
    setIsAsking(true);
    setAskAnswer('');
    try {
      const res = await api.post('/lease-qa/ask', { question: askInput });
      setAskAnswer(res.data.answer);
    } catch {
      setAskAnswer('Error getting answer. Is the Q&A service running?');
    } finally {
      setIsAsking(false);
    }
  };

  if (!data) return <div className="text-gray-500 font-mono p-8">Loading templates...</div>;

  const exampleQuestions = [
    "What is the late fee policy?",
    "What are the tenant maintenance responsibilities?",
    "What's the pet policy?",
    "What happens if a tenant doesn't pay rent?",
    "What are the emergency repair procedures?",
    "What's the guest policy?",
    "What does the lease say about smoking?",
    "How much notice is required to vacate?",
  ];

  return (
    <div className="space-y-5 pb-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white font-mono flex items-center gap-3">
          <span className="text-cyan-400">~/</span>lease-templates
        </h1>
        <p className="text-gray-500 text-sm mt-1 font-mono">
          Default lease templates &mdash; {data.total_leases} active leases, {data.missing_pdf_count} missing PDFs
        </p>
      </div>

      {/* Warning if missing PDFs */}
      {data.missing_pdf_count > 0 && (
        <div className="bg-red-900/10 border border-red-700/20 rounded-lg p-4">
          <div className="text-red-400 text-xs font-mono font-bold uppercase tracking-wider mb-2">
            {data.missing_pdf_count} Tenants Missing Lease PDFs
          </div>
          {data.missing_pdfs.map((t, i) => (
            <div key={i} className="flex items-center gap-3 py-1">
              <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
              <span className="text-gray-300 text-sm font-mono">{t.tenant}</span>
              <span className="text-gray-500 text-xs font-mono">{t.address}</span>
            </div>
          ))}
        </div>
      )}

      {/* Template Cards */}
      <div className="space-y-3">
        {data.templates.map((tmpl, idx) => (
          <div key={idx} className="bg-gray-800/30 border border-gray-700/30 rounded-lg overflow-hidden">
            {/* Template Header */}
            <div
              className="px-4 py-3 flex items-center gap-4 cursor-pointer hover:bg-gray-800/20"
              onClick={() => setExpandedTemplate(expandedTemplate === idx ? null : idx)}
            >
              <span className="text-cyan-400 text-lg font-mono flex-shrink-0">
                {expandedTemplate === idx ? 'v' : '>'}
              </span>
              <div className="flex-1">
                <div className="text-white font-mono text-sm font-semibold">{tmpl.name}</div>
                <div className="text-gray-500 text-xs font-mono mt-0.5">{tmpl.description}</div>
              </div>
              <span className="text-gray-400 text-xs font-mono">{tmpl.pages} pages</span>
              <span className="text-gray-400 text-xs font-mono bg-gray-800/50 px-2 py-1 rounded">
                {tmpl.count || 0} tenants
              </span>
              {tmpl.pdf_url && (
                <button
                  onClick={(e) => { e.stopPropagation(); setPreviewPdf({ url: tmpl.pdf_url!, name: tmpl.name }); }}
                  className="text-[10px] font-mono text-cyan-400 bg-cyan-900/20 px-2.5 py-1 rounded border border-cyan-700/30 hover:bg-cyan-800/30 transition-colors"
                >
                  View Template
                </button>
              )}
            </div>

            {/* Expanded - show tenants using this template */}
            {expandedTemplate === idx && tmpl.tenants && tmpl.tenants.length > 0 && (
              <div className="border-t border-gray-700/20">
                <div className="px-4 py-2 bg-gray-800/10">
                  <div className="text-gray-500 text-[10px] font-mono uppercase tracking-wider">Tenants using this template</div>
                </div>
                <div className="divide-y divide-gray-800/20">
                  {tmpl.tenants.map((t, i) => (
                    <div key={i} className="px-4 py-2 flex items-center gap-3 hover:bg-gray-800/20 text-sm">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${t.has_pdf === false ? 'bg-red-400' : 'bg-green-400'}`} />
                      <span className="text-gray-200 font-mono flex-1">{t.tenant}</span>
                      <span className="text-gray-500 text-xs font-mono">{t.address?.split(',')[0]}</span>
                      {t.rent ? (
                        <span className="text-gray-400 font-mono text-xs">{fmt(t.rent)}/mo</span>
                      ) : null}
                      {t.pdf_url ? (
                        <button
                          onClick={() => setPreviewPdf({ url: t.pdf_url!, name: `${t.tenant} - Lease` })}
                          className="text-gray-500 hover:text-cyan-400 text-xs font-mono transition-colors"
                        >
                          View PDF
                        </button>
                      ) : (
                        <span className="text-red-400 text-[10px] font-mono">NO PDF</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Ask Questions Section */}
      <div className="bg-gray-800/30 border border-gray-700/30 rounded-lg p-4 space-y-3">
        <div className="text-white font-mono text-sm font-semibold">Ask about the lease templates</div>
        <div className="flex gap-2">
          <input
            type="text"
            value={askInput}
            onChange={e => setAskInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && askQuestion()}
            placeholder="e.g., What is the late fee policy?"
            className="flex-1 bg-gray-800/50 border border-gray-700/50 rounded-lg px-3 py-2 text-gray-200 font-mono text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-700/50"
          />
          <button
            onClick={askQuestion}
            disabled={isAsking || !askInput.trim()}
            className="bg-cyan-900/50 hover:bg-cyan-800/50 border border-cyan-700/50 text-cyan-400 font-mono text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-40"
          >
            {isAsking ? 'Ask' : 'Ask'}
          </button>
        </div>

        {/* Thinking animation */}
        {isAsking && (
          <div className="flex items-center gap-3 py-3 px-4 bg-gray-800/30 border border-cyan-700/20 rounded-lg">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-cyan-400/70 text-sm font-mono animate-pulse">Searching lease documents...</span>
          </div>
        )}

        {/* Example questions */}
        {!askAnswer && !isAsking && (
          <div className="flex flex-wrap gap-1.5">
            {exampleQuestions.map((q, i) => (
              <button
                key={i}
                onClick={() => { setAskInput(q); }}
                className="text-[11px] font-mono text-gray-500 bg-gray-800/40 hover:bg-gray-800/70 px-2.5 py-1 rounded border border-gray-700/30 hover:text-gray-300 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Answer */}
        {askAnswer && (
          <div className="bg-gray-800/50 border border-gray-700/30 rounded-lg p-3">
            <div
              className="font-mono text-sm text-gray-300 leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={{
                __html: askAnswer
                  .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')
                  .replace(/\n/g, '<br/>')
              }}
            />
          </div>
        )}
      </div>

      {/* PDF Preview Modal */}
      {previewPdf && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreviewPdf(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-5xl h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
              <span className="text-white font-mono text-sm truncate flex-1 mr-4">{previewPdf.name}</span>
              <div className="flex gap-2 flex-shrink-0">
                <a href={previewPdf.url} download className="text-xs font-mono text-cyan-400 bg-cyan-900/20 px-3 py-1 rounded border border-cyan-700/30 hover:bg-cyan-800/30">Download</a>
                <button onClick={() => setPreviewPdf(null)} className="text-gray-400 hover:text-white text-lg px-2">&times;</button>
              </div>
            </div>
            <iframe src={previewPdf.url} className="flex-1 w-full bg-white rounded-b-xl" title="Document Preview" />
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaseTemplates;
